import React from "react";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Heading,
	If,
	makeClassName,
	Modal,
	Row,
	StyleProps,
	Tooltip,
} from "@hex-insights/core";
import { anyFieldChanged, FormState, SubmitButton, useFieldState } from "@hex-insights/forms";
import { SharingPolicyDetailQuery, useUserGroupDetailedSelectListQuery } from "../../../../../../Utilities";
import { SharingPolicyForm } from "../../../../../Forms";
import { SharingPolicyCustomFormValues } from "../../../../../FormUtilities";
import { SharingMenuContext, SharingMenuFormValues, useHasAdminSharingPermission, useSetIsPublic } from "../../utils";
import { AdvancedSharingModal } from "../Advanced";
import { CustomSharingButton, PrivateOption, PublicOption, UserGroupOption } from "../General";
import styles from "./styles.module.css";

export type SharingTooltipProps = SimpleSharingContentProps & Partial<ClassNameProps & StyleProps>;

export function SharingTooltip({ formState, isOpen, className, style }: SharingTooltipProps) {
	return (
		<Tooltip isInteractable className={makeClassName(styles["sharing-tooltip"], className)} style={style}>
			<Tooltip.Body className={styles["sharing-tooltip__body"]}>
				<SimpleSharingContent formState={formState} isOpen={isOpen} />
			</Tooltip.Body>
		</Tooltip>
	);
}

export type SimpleSharingModalProps = {
	sharingPolicy: Omit<SharingPolicyDetailQuery["sharingPolicy"], SharingPolicyCustomFormValues.InstanceParentFields>;
	onClose: () => void;
};

export function SimpleSharingModal({ sharingPolicy, onClose }: SimpleSharingModalProps) {
	const { formState, onSubmit } = SharingPolicyForm.useDetailForm<keyof SharingPolicyCustomFormValues.DetailInParent>({
		sharingPolicy,
		fields: SharingPolicyCustomFormValues.detailInParentFieldInclusionMap,
		onSuccess: onClose,
	});

	const anyChanged = anyFieldChanged(formState);

	return (
		<Modal onClose={onClose} confirmOnClose={anyChanged} className={styles["sharing-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Share With</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<SimpleSharingContent formState={formState} isOpen />
			</Modal.Body>
			<If condition={anyChanged}>
				<Modal.Footer>
					<Row justify="space-between" align="center">
						<Button variant="link" onClick={onClose}>
							Cancel
						</Button>

						<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
							Save Changes
						</SubmitButton>
					</Row>
				</Modal.Footer>
			</If>
		</Modal>
	);
}

type SimpleSharingContentProps = {
	formState: FormState<SharingMenuFormValues>;
	isOpen: boolean;
};

function SimpleSharingContent({ formState, isOpen }: SimpleSharingContentProps) {
	useSetIsPublic(formState);

	const isPublicFieldState = useFieldState<SharingMenuFormValues, SharingMenuFormValues["isPublic"]>({
		formState,
		name: "isPublic",
	});
	const userGroupIDsFieldState = useFieldState<SharingMenuFormValues, SharingMenuFormValues["userGroupIDs"]>({
		formState,
		name: "userGroupIDs",
	});
	const userIDsFieldState = useFieldState<SharingMenuFormValues, SharingMenuFormValues["userIDs"]>({
		formState,
		name: "userIDs",
	});

	const { data } = useUserGroupDetailedSelectListQuery({
		variables: {
			filters: {
				or: [
					{
						members: [
							{
								isCurrentEQ: true,
							},
						],
					},
					// {
					// 	idIn: userGroupIDsFieldState.value, // TODO
					// },
				],
			},
		},
		skip: !isOpen,
	});

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const hasAdminPermission = useHasAdminSharingPermission(formState.formType);
	const contextValue = React.useMemo(
		() => ({
			hasAdminPermission,
		}),
		[hasAdminPermission],
	);

	return (
		<SharingMenuContext.Provider value={contextValue}>
			<Column justify="spaced-start" verticalSpacing="0.25rem">
				<CustomSharingButton formState={formState} onClick={openModal} />

				<If condition={hasAdminPermission}>
					<PublicOption fieldState={isPublicFieldState} formState={formState} />
				</If>
				<PrivateOption fieldState={isPublicFieldState} formState={formState} />

				<SimpleSharingSectionLabel>Groups</SimpleSharingSectionLabel>
				{data?.userGroupConnection.edges.map((e) => (
					<UserGroupOption key={e.node.id} fieldState={userGroupIDsFieldState} userGroup={e.node} />
				))}
			</Column>

			<Modal.If condition={isModalOpen}>
				<AdvancedSharingModal
					formState={formState}
					isPublicFieldState={isPublicFieldState}
					userGroupIDsFieldState={userGroupIDsFieldState}
					userIDsFieldState={userIDsFieldState}
					onClose={closeModal}
				/>
			</Modal.If>
		</SharingMenuContext.Provider>
	);
}

export type SimpleSharingSectionLabelProps = Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function SimpleSharingSectionLabel({ className, style, children }: SimpleSharingSectionLabelProps) {
	return (
		<div className={makeClassName(styles["sharing-tooltip__section-label"], className)} style={style}>
			{children}
		</div>
	);
}
